import { ApplicationSubmissionMethod } from '@zorro/clients';
import { VALIDATION_MESSAGES } from '@zorro/common-utils';
import {
  DisplayPlanMarket,
  UploadableBenefitDocument,
} from '@zorro/web/shared-ui';
import * as yup from 'yup';

const requiredEvidenceSchema = yup
  .mixed<UploadableBenefitDocument[]>()
  .required(VALIDATION_MESSAGES.documentRequired)
  .test(
    'is-not-empty',
    VALIDATION_MESSAGES.documentRequired,
    (value) => Array.isArray(value) && value.length > 0
  );

const optionalEvidenceSchema = yup
  .mixed<UploadableBenefitDocument[]>()
  .notRequired()
  .nullable();

export const CARRIER_APPLICATION_SUBMIT_TYPES = [
  ApplicationSubmissionMethod.MANUAL,
  ApplicationSubmissionMethod.PDF,
  ApplicationSubmissionMethod.DEEPLINK,
  ApplicationSubmissionMethod.ENROLL_CONNECT,
] as const;

export const getCarrierApplicationSubmitSchema = (isEditMode: boolean) =>
  yup.object({
    applicationEvidences: yup
      .mixed<UploadableBenefitDocument[]>()
      .when('noApplicationEvidence', {
        is: true,
        then: () => optionalEvidenceSchema,
        otherwise: () => requiredEvidenceSchema,
      }),
    noApplicationEvidence: yup.boolean(),
    brokerAttestationAccepted: yup.boolean(),
    proofOfConsentDocuments: optionalEvidenceSchema,
    specialEnrollmentDocuments: optionalEvidenceSchema,
    withIchraLetterAsSepProof: yup.boolean(),
    planMarket: yup
      .mixed<DisplayPlanMarket>()
      .oneOf(
        Object.values(DisplayPlanMarket),
        VALIDATION_MESSAGES.planMarketRequired
      )
      .nullable()
      .required(VALIDATION_MESSAGES.planMarketRequired)
      // EnrollConnect is off-exchange only; the field is hidden for it and can be emptied by a form reset.
      .when('submissionMethod', {
        is: ApplicationSubmissionMethod.ENROLL_CONNECT,
        then: (schema) => schema.notRequired(),
      }),
    includeApplicationInstructionsEmail: yup.boolean().required(),
    submissionMethod: isEditMode
      ? yup
          .mixed<ApplicationSubmissionMethod>()
          .oneOf(Object.values(ApplicationSubmissionMethod))
          .notRequired()
      : yup
          .mixed<ApplicationSubmissionMethod>()
          .oneOf(CARRIER_APPLICATION_SUBMIT_TYPES)
          .required(),
  });

export type CarrierApplicationSubmitFormFields = yup.InferType<
  ReturnType<typeof getCarrierApplicationSubmitSchema>
>;

export type CheckboxChangeHandler = (
  onChange: (value: boolean) => void,
  eventName:
    | 'include_application_instructions_email'
    | 'include_payment_instructions_email'
) => (event: React.ChangeEvent<HTMLInputElement>) => void;
